import { miner, MinerSubset } from '@cbre/digger';
import { EMPTY_STRING } from '@miq/utils';
import { isEmptyObject, isNull } from '@shared/functions';

/**
 * Represents a subset of a postal address.
 */
interface PostalAddressSubset extends MinerSubset {
  streetName: string;
  district?: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * Retrieves the postal address subset from the given data object.
 * @param data The data object containing the postal address information.
 * @param defaultText The default text to use if any address field is missing.
 * @returns The postal address subset.
 */
const getPostalAddress = (data: Record<string, any>): PostalAddressSubset => {
  const postalAddressSubset: PostalAddressSubset = {
    streetName: 'full_street_name',
    district: 'district',
    city: 'city',
    state: 'state_province',
    zip: 'postal_code',
  };
  const {
    streetName = EMPTY_STRING,
    city = EMPTY_STRING,
    state = EMPTY_STRING,
    zip = EMPTY_STRING,
  } = miner(data, postalAddressSubset);

  return { streetName, city, state, zip };
};

/**
 * Converts a postal address object to a printable string representation.
 * @param postalAddress The postal address object.
 * @param defaultText The default text to use if the postal address is null or empty.
 * @param addNewLine Indicates whether to add a new line between address lines.
 * @returns The string representation of the postal address.
 */
export const stringifyPostalCodeAddress = (
  postalAddress: Record<string, any>,
  defaultText = '-',
  addNewLine = false
): string => {
  if (isEmptyObject(postalAddress)) {
    return 'None';
  }

  const { streetName, city, state, zip } = getPostalAddress(postalAddress);

  const displayFields = [streetName, city, state];
  const fullAddress = `${displayFields.filter(w => !!w).join(', ')} ${zip || ''}`;
  if (addNewLine) {
    const [first, ...rest] = displayFields.filter(w => !!w);
    const fullAddressWithNewLine = `${first + '\n' + rest.join(', ')} ${zip}`;
    return !!fullAddressWithNewLine ? fullAddressWithNewLine : defaultText;
  }
  return !!fullAddress ? fullAddress : defaultText;
};

/**
 * Represents different address styles for stringifying postal addresses.
 */
export enum AddressStyles {
  Default = 0,
  NoPostalCode,
  TwoLines,
}

/**
 * Options for customizing the stringification of postal addresses.
 */
export interface StringifyPostalCodeAddressOptions {
  defaultText?: string;
  template?: AddressStyles;
}

const defaultOptions = { defaultText: '-', addNewLine: false, template: AddressStyles.Default };
/**
 * Converts a postal address object to a string representation using the specified options.
 * @param postalAddress The postal address object.
 * @param options The options for customizing the stringification.
 * @returns The string representation of the postal address.
 * @throws Error if an invalid template is provided.
 */
export const stringifyPostalCodeAddressV2 = (
  postalAddress: Record<string, any>,
  options: StringifyPostalCodeAddressOptions = {}
): string => {
  const params = { ...defaultOptions, ...options };
  const { template, defaultText } = params;

  if (isEmptyObject(postalAddress)) {
    return defaultText;
  }

  const templateFn = AddressStylesTemplates[template];

  if (!templateFn) {
    throw new Error('Invalid template');
  }
  const addressData = getPostalAddress(postalAddress);

  if (Object.values(addressData).every(v => isNull(v))) {
    return defaultText;
  }

  const result = templateFn(getPostalAddress(postalAddress));
  return result;
};

/**
 * Templates for different address styles.
 */
export const AddressStylesTemplates = {
  [AddressStyles.Default]: ({ streetName, city, state, zip }: PostalAddressSubset) =>
    `${streetName}, ${city}, ${state} ${zip}`,

  [AddressStyles.TwoLines]: ({ streetName, city, state, zip }: PostalAddressSubset) =>
    `${streetName},\n ${city}, ${state} ${zip}`,

  [AddressStyles.NoPostalCode]: ({ streetName, city, state }: PostalAddressSubset) =>
    `${streetName}, ${city}, ${state}`,
};
